import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Tag, Gift, Handshake, MapPinned, Network, Store, Layers, Sprout, Info } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { CaseVisual } from "../components/CaseVisual";

const ICONS = { tag: Tag, gift: Gift, handshake: Handshake, map: MapPinned, network: Network, store: Store, layers: Layers, sprout: Sprout };

const Heading = ({ children }) => (
    <h2 className="font-serif font-semibold text-ink tracking-tight leading-snug text-2xl sm:text-3xl">{children}</h2>
);

export default function CaseStudy({ c }) {
    return (
        <div data-testid={`case-study-${c.slug}`}>
            <Seo title={c.seo.title} description={c.seo.description} path={`/our-results/${c.slug}`} />

            {/* Header */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-14 sm:pb-16">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Link to="/our-results" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-400 transition-colors duration-200" data-testid="cs-back">
                        <ArrowLeft size={15} /> All results
                    </Link>
                    <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                        <FadeUp className="lg:col-span-7">
                            <p className="eyebrow">{c.kind} · {c.client}</p>
                            <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-3xl sm:text-[2.6rem]">{c.title}</h1>
                            <dl className="mt-8 grid gap-4 sm:grid-cols-2 border-t border-white/10 pt-6">
                                {c.meta.map((m) => (
                                    <div key={m.label} className={m.value.length > 40 ? "sm:col-span-2" : ""}>
                                        <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">{m.label}</dt>
                                        <dd className="mt-1.5 text-sm text-slate-200 leading-relaxed">{m.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </FadeUp>
                        <FadeUp delay={0.1} className="lg:col-span-5">
                            <CaseVisual c={c} className="aspect-[4/3]" />
                        </FadeUp>
                    </div>
                </div>
            </section>

            <section className="bg-cream section-pad">
                <div className="max-w-4xl mx-auto px-5 sm:px-8">
                    {/* Intro */}
                    <FadeUp>
                        <Heading>{c.intro.heading}</Heading>
                        <div className="mt-5 space-y-4">
                            {c.intro.paragraphs.map((p) => (
                                <p key={p} className="text-ink/85 leading-relaxed text-base sm:text-lg">{p}</p>
                            ))}
                        </div>
                    </FadeUp>

                    {/* What made the difference: 2x2 on desktop, stacked on mobile */}
                    {c.highlights && (
                        <div className="mt-14" data-testid="cs-highlights">
                            <FadeUp>
                                <Heading>{c.highlights.heading}</Heading>
                            </FadeUp>
                            <Stagger className="mt-7 grid sm:grid-cols-2 gap-5">
                                {c.highlights.items.map((h, i) => {
                                    const Icon = ICONS[h.icon] || Info;
                                    return (
                                        <StaggerItem key={h.title} className="h-full">
                                            <div className="h-full bg-white border border-navy-900/10 rounded-xl p-7 shadow-[0_1px_3px_rgba(11,21,38,0.06)]" data-testid={`cs-highlight-${i + 1}`}>
                                                <div className="w-11 h-11 rounded-lg bg-navy-900 flex items-center justify-center">
                                                    <Icon size={20} className="text-gold-400" />
                                                </div>
                                                <h3 className="mt-5 font-serif font-semibold text-ink text-xl">{h.title}</h3>
                                                <p className="mt-2 text-warmgrey leading-relaxed">{h.desc}</p>
                                            </div>
                                        </StaggerItem>
                                    );
                                })}
                            </Stagger>
                        </div>
                    )}

                    {/* Body sections */}
                    {c.sections.map((sec) => (
                        <FadeUp key={sec.heading} className="mt-14 pt-12 border-t border-navy-900/10">
                            <Heading>{sec.heading}</Heading>
                            {sec.paragraphs && (
                                <div className="mt-5 space-y-4">
                                    {sec.paragraphs.map((p) => (
                                        <p key={p} className="text-ink/85 leading-relaxed">{p}</p>
                                    ))}
                                </div>
                            )}
                            {sec.steps && (
                                <ol className="mt-7 space-y-6">
                                    {sec.steps.map((st, i) => (
                                        <li key={st.title} className="flex gap-5">
                                            <span className="font-serif font-semibold text-2xl text-gold-600 w-8 shrink-0 leading-none pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                                            <div>
                                                <h3 className="font-sans font-semibold text-ink">{st.title}</h3>
                                                <p className="mt-1.5 text-warmgrey leading-relaxed">{st.text}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </FadeUp>
                    ))}

                    {c.metrics && (
                        <FadeUp className="mt-10 grid grid-cols-3 gap-4 border-t border-navy-900/10 pt-6">
                            {c.metrics.map((m) => (
                                <div key={m.label}>
                                    <p className="font-serif font-semibold text-xl sm:text-2xl text-gold-700">{m.value}</p>
                                    <p className="text-[10px] font-mono tracking-widest uppercase text-navy-900/50 mt-1.5">{m.label}</p>
                                </div>
                            ))}
                        </FadeUp>
                    )}

                    {/* Lesson + connection */}
                    {c.lesson && (
                        <FadeUp className="mt-14">
                            <div className="bg-navy-900 rounded-xl p-8 sm:p-10">
                                <p className="eyebrow">The Commercial Lesson</p>
                                <p className="mt-4 font-serif text-xl sm:text-2xl text-white leading-snug">{c.lesson}</p>
                                {c.connection && (
                                    <p className="mt-6 pt-6 border-t border-white/10 text-slate-300/90 leading-relaxed">
                                        <span className="font-semibold text-gold-400">The GrowVise connection. </span>
                                        {c.connection}
                                    </p>
                                )}
                            </div>
                        </FadeUp>
                    )}

                    {c.disclosure && (
                        <p className="mt-8 flex gap-3 text-sm text-navy-900/60 leading-relaxed bg-mist border border-navy-900/10 rounded-lg px-5 py-4" data-testid="cs-disclosure">
                            <Info size={16} className="shrink-0 mt-0.5 text-gold-700" />
                            <span><span className="font-semibold text-ink/80">Disclosure: </span>{c.disclosure}</span>
                        </p>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-navy-900 border-t border-white/10" data-testid="cs-detail-cta">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
                    <FadeUp className="flex-1">
                        <h2 className="font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Ready to apply this thinking to your business?
                        </h2>
                        <p className="mt-4 text-slate-300/85 leading-relaxed max-w-xl">
                            A free 30-minute Growth Conversation about pricing, channels and execution. Practical, confidential and no obligation.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="flex flex-wrap gap-4 shrink-0">
                        <Link to="/book-a-conversation" className="btn-gold" data-testid="cs-detail-book-cta">
                            Discuss Your Growth Opportunities <ArrowRight size={16} />
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
